import { PARTIES, PARTY_ORDER, type CountyAggregate, type PartyId, type ValkrtsAggregate } from '../lib/types';

export interface ElectionSection {
  title: string;
  results: Partial<Record<PartyId, number>>;
}

interface Props {
  name: string;
  aggregate: ValkrtsAggregate | CountyAggregate | null;
  electionSections: ElectionSection[];
  onClose: () => void;
  onZoomIn: () => void;
}

const nf = new Intl.NumberFormat('sv-SE');

function PartyRow({
  party,
  value,
  total,
  isPercent,
}: {
  party: PartyId;
  value: number;
  total: number;
  isPercent: boolean;
}) {
  const displayValue = isPercent
    ? `${value.toFixed(1)}%`
    : total > 0
    ? `${Math.round((value / total) * 100)}% (${nf.format(value)})`
    : '0%';

  return (
    <div className="region-popup-stat-row">
      <div className="region-popup-stat-label">
        <span className="color-dot" style={{ backgroundColor: PARTIES[party].color }} />
        <span>{PARTIES[party].name}</span>
      </div>
      <span className="region-popup-stat-value">{displayValue}</span>
    </div>
  );
}

export function RegionPopup({ name, aggregate, electionSections, onClose, onZoomIn }: Props) {
  const totalPop = aggregate?.total_population ?? 0;

  const gameEntries = aggregate
    ? PARTY_ORDER
        .map(p => ({ p, v: aggregate.followerCounts[p] ?? 0 }))
        .filter(({ v }) => v > 0)
        .sort((a, b) => b.v - a.v)
    : null;

  return (
    <div className="region-popup">
      <div className="region-popup-header">
        <h3 className="region-popup-title">{name}</h3>
        <button className="region-popup-close" onClick={onClose} aria-label="Stäng">✕</button>
      </div>

      <div className="region-popup-body">
        <div className="region-popup-section">
          <p className="region-popup-section-label">I spelet</p>
          {gameEntries === null ? (
            <p className="region-popup-muted">Laddar aggregerat data…</p>
          ) : gameEntries.length === 0 ? (
            aggregate?.ownedByParty ? (
              <p className="region-popup-muted">
                Majoritet:{' '}
                <span style={{ color: PARTIES[aggregate.ownedByParty].color, fontWeight: 600 }}>
                  {PARTIES[aggregate.ownedByParty].name}
                </span>
              </p>
            ) : (
              <p className="region-popup-muted">Inga röster registrerade än</p>
            )
          ) : (
            gameEntries.map(({ p, v }) => (
              <PartyRow key={p} party={p} value={v} total={totalPop} isPercent={false} />
            ))
          )}
        </div>

        {electionSections.map(sec => (
          <div key={sec.title} className="region-popup-section">
            <p className="region-popup-section-label">{sec.title}</p>
            {PARTY_ORDER
              .map(p => ({ p, v: sec.results[p] ?? 0 }))
              .sort((a, b) => b.v - a.v)
              .map(({ p, v }) => (
                <PartyRow key={p} party={p} value={v} total={0} isPercent />
              ))}
          </div>
        ))}
      </div>

      <div className="region-popup-footer">
        <button className="region-popup-zoom-btn" onClick={onZoomIn}>Zooma in 🔍</button>
        <button className="region-popup-close-btn" onClick={onClose}>Stäng</button>
      </div>
    </div>
  );
}
